import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BulkAddGroupMembersDialog from './bulk-add-group-members-dialog';
import ChangeGroupMemberRoleDialog from './change-group-member-role-dialog';
import CloneGroupMembersDialog from './clone-group-members-dialog';
import DeleteGroupMembersDialog from './delete-group-members-dialog';
import { Messages } from '../../../../utils/message-constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserKeys, getAllGroupMembers } from '../../../../api/users-api';
import {
  isAbleToSeeUIElement,
  DirectoryRoles,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { userPortalPagesConfig } from '../../../../shared/user-portal-pages-config';
import { DirectoryGroup } from '../../../../../types';
import { pagesConfig } from '../../../../shared/pages-config';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import GroupRemove from '@mui/icons-material/GroupRemove';
import Groups from '@mui/icons-material/Groups';
import Person from '@mui/icons-material/Person';
import Visibility from '@mui/icons-material/Visibility';

interface GroupMembershipGridProps {
  group: DirectoryGroup;
}

export default function GroupMembershipGrid({
  group,
}: GroupMembershipGridProps) {
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const { groupEmail } = useParams() as { groupEmail: string };
  const { directoryRoles, changeLogRoles, userViewRoles } = useUserSettings();
  const queryClient = useQueryClient();
  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [openDeleteMembersFromGroup, setOpenDeleteMembersFromGroup] =
    useState(false);
  const [openCloneGroupMembers, setOpenCloneGroupMembers] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openChangeRoles, setOpenChangeRoles] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.GroupMembers, groupEmail],
    queryFn: () => getAllGroupMembers(groupEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group members'),
    },
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          const emailDisplay =
            params.row.email === '*'
              ? 'All current and future users within domain'
              : params.row.email;
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="body2">{emailDisplay}</Typography>
              {params.row.type === 'group' ? (
                <Tooltip arrow title="View Group">
                  <span>
                    <IconButton
                      onClick={() =>
                        navigate(
                          `/${pagesConfig.groups.link}/${params.row.email}`
                        )
                      }
                    >
                      <Visibility />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 1,
        display: 'flex',
        valueGetter: (value: string) => {
          return value
            ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
            : '';
        },
        type: 'singleSelect',
        valueOptions: ['Owner', 'Manager', 'Member'],
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: ['user', 'group'],
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {params.row.type === 'group' ? (
                <Tooltip arrow title="Group">
                  <Groups />
                </Tooltip>
              ) : (
                <Tooltip arrow title="User">
                  <Person />
                </Tooltip>
              )}
            </Stack>
          );
          return avatar;
        },
      },
    ],
    [navigate]
  );

  const isAbleToAddMembers = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.AddMemberToGroup.value
  );

  const AddMemberRoleTooltip = !isAbleToAddMembers
    ? Messages.Errors.Permission.Acting(DirectoryRoles.AddMemberToGroup.name)
    : 'Add Member';

  const CloneMemberRoleTooltip = !isAbleToAddMembers
    ? Messages.Errors.Permission.Acting(DirectoryRoles.AddMemberToGroup.name)
    : 'Add Members From Group';

  const EditMemberRoleTooltip = !isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupMemberRole.value
  )
    ? Messages.Errors.Permission.Acting(DirectoryRoles.EditGroupMemberRole.name)
    : 'Edit Roles';

  const RemoveMemberRoleTooltip = !isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.RemoveMemberFromGroup.value
  )
    ? Messages.Errors.Permission.Acting(
        DirectoryRoles.RemoveMemberFromGroup.name
      )
    : 'Delete';

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddMembers(true)}
            disabled={!isAbleToAddMembers}
            label="Add Member"
            tooltipLabel={AddMemberRoleTooltip}
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="edit"
              onClick={() => setOpenChangeRoles(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.EditGroupMemberRole.value
                ) ||
                selectedRows.includes('*')
              }
              tooltipLabel={EditMemberRoleTooltip}
            />
            <SharedIconButton
              variant="cloneMember"
              onClick={() => setOpenCloneGroupMembers(true)}
              disabled={
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.AddMemberToGroup.value
                )
              }
              tooltipLabel={CloneMemberRoleTooltip}
            />
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteMembersFromGroup(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.RemoveMemberFromGroup.value
                )
              }
              tooltipLabel={RemoveMemberRoleTooltip}
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.GroupMembers, groupEmail],
                });
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.DirectoryGroup, groupEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      ></SharedToolbar>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Members', <GroupRemove />),
        }}
        getRowId={(row) => row.email}
        rows={data || []}
        columns={columns}
        loading={isLoading || isFetching}
        disableColumnSelector
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {selectedRows.length > 0 && openDeleteMembersFromGroup ? (
        <DeleteGroupMembersDialog
          open={openDeleteMembersFromGroup}
          handleClose={() => setOpenDeleteMembersFromGroup(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          userView={userView}
          isCloudIdentityEnabled={false}
          cloudIdentityGroupMembers={[]}
        />
      ) : null}
      {selectedRows.length > 0 && openChangeRoles ? (
        <ChangeGroupMemberRoleDialog
          open={openChangeRoles}
          handleClose={() => setOpenChangeRoles(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          userView={userView}
        />
      ) : null}
      {!userView && openCloneGroupMembers ? (
        <CloneGroupMembersDialog
          open={openCloneGroupMembers}
          handleClose={() => setOpenCloneGroupMembers(false)}
          groupEmail={groupEmail}
          group={group}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openAddMembers ? (
        <BulkAddGroupMembersDialog
          userView={userView}
          open={openAddMembers}
          handleClose={() => setOpenAddMembers(false)}
          groupEmail={groupEmail}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          directoryRoles={directoryRoles}
          userViewRoles={userViewRoles}
        />
      ) : null}
    </>
  );
}
